import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Heart,
  X,
  HeartHandshake,
  Sparkles,
  Loader2,
  Search,
  Link2,
  Users,
  UserCog,
  Copy,
  HeartCrack,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AvatarCropDialog } from "@/components/avatar-crop-dialog";
import { BoideSettingsDialog } from "@/components/boide-settings-dialog";
import { LocationPicker } from "@/components/location-picker";
import { ViewableAvatar, bandColor, FullProfileBody } from "@/components/matchmaking/full-profile";
import { useBoideUser, refreshBoideUser } from "@/hooks/use-boide-user";
import { useRegisterTour } from "@/components/tour/tour-provider";
import {
  getProfile,
  uploadAvatar,
  getMatchProfile,
  getMatchMeta,
  upsertMatchProfile,
  deactivateMatchProfile,
  uploadMatchPhotos,
  deleteMatchPhoto,
  reorderMatchPhotos,
  getMatchFeed,
  swipeMatch,
  undoSwipe,
  searchMatch,
  createMatchInvite,
  acceptMatchInvite,
  listMatchConnections,
  getMatchConnection,
  disconnectMatchConnection,
  getCompatReading,
  castCompatReading,
  type BoideProfile,
  type SocialLinks,
  type MatchIntent,
  type MatchFeedItem,
  type FieldVisibilityMap,
  type ShareableField,
  type MatchConnectionSummary,
  type LocationValue,
  type GoalTagsMap,
} from "@/lib/boide-api";
import {
  MapPin,
  Check,
  SlidersHorizontal,
  Target,
  RefreshCw,
  Undo2,
  Briefcase,
  ImagePlus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  EyeOff,
  Camera,
  AlertCircle,
  Pencil,
} from "lucide-react";
import { AiWatermark } from "@/components/ai-watermark";

type SeDuyenSearch = { invite?: string };

export const Route = createFileRoute("/_authenticated/se-duyen/")({
  validateSearch: (search: Record<string, unknown>): SeDuyenSearch => ({
    invite: typeof search.invite === "string" ? search.invite : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Se Duyên & Hợp Tác Làm Ăn - Xem Tương Hợp Tử Vi, Ngũ Hành" },
      {
        name: "description",
        content:
          "Kết nối hữu duyên dựa trên tương hợp Tử Vi, Bát Tự, vị trí địa lý, độ tuổi & mục tiêu sống: se duyên đôi lứa & tìm người hợp tác làm ăn. Khám phá ngay!",
      },
    ],
  }),
  component: SeDuyenPage,
});

const MD_COMPONENTS = {
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-[#E5B869]">{children}</strong>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="font-serif-soft italic text-[#E5B869] text-xl mt-6 mb-3">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="font-serif-soft italic text-[#E5B869] text-lg mt-5 mb-2">{children}</h3>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="flex gap-2 items-start leading-[1.7]">
      <span className="text-[#E5B869] text-[12px] mt-[5px] opacity-80 select-none">✦</span>
      <div className="flex-1">{children}</div>
    </li>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="flex flex-col gap-2 my-3 list-none !pl-0 !ml-0">{children}</ul>
  ),
};

const INTENTS: { key: MatchIntent; label: string; icon: typeof Heart }[] = [
  { key: "se_duyen", label: "Se duyên", icon: Heart },
  { key: "lam_an", label: "Hợp tác làm ăn", icon: HeartHandshake },
];

/** Nút mắt nhỏ cạnh mỗi field — bật/tắt hiển thị công khai (mặc định: hiển thị). */
function VisibilityToggle({
  visible,
  onToggle,
  label,
}: {
  visible: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? `Ẩn ${label}` : `Hiện ${label}`}
      title={visible ? `Đang hiển thị — bấm để ẩn` : `Đang ẩn — bấm để hiện`}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition ${
        visible
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border/40 bg-background/40 text-muted-foreground"
      }`}
    >
      {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
    </button>
  );
}

/** Ô nhập khoảng tuổi mong muốn (dùng chung cho se duyên & làm ăn). */
function AgeRange({
  label,
  min,
  max,
  onMin,
  onMax,
}: {
  label: string;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
}) {
  return (
    <div>
      <Label className="mb-2 block text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          min={18}
          max={99}
          value={min}
          onChange={(e) => onMin(e.target.value)}
          placeholder="Từ"
          className="flex-1"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="number"
          inputMode="numeric"
          min={18}
          max={99}
          value={max}
          onChange={(e) => onMax(e.target.value)}
          placeholder="Đến"
          className="flex-1"
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Để trống nếu không giới hạn. Ngoài khoảng này vẫn hiện nhưng xếp hạng thấp hơn.
      </p>
    </div>
  );
}

function SeDuyenPage() {
  const { invite } = Route.useSearch();
  const [intent, setIntent] = useState<MatchIntent>("se_duyen");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const profile = useQuery({ queryKey: ["mm-profile"], queryFn: getMatchProfile });
  const active = !!profile.data?.is_active;

  const justActivatedProfileRef = useRef(false);
  const [activeTab, setActiveTab] = useState("kham-pha");

  // Register tour for Se Duyên based on active status
  useRegisterTour(active ? "seduyen-discover" : "seduyen", !profile.isLoading);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToStep = (tourId: string, stepIndex: number) => {
      if (tourId === "seduyen-discover") {
        setActiveTab("kham-pha");
      }

      let stepSelectors: string[] = [];
      if (tourId === "seduyen") {
        stepSelectors = [
          "body",
          '[data-tour="seduyen-personal-info"]',
          '[data-tour="seduyen-purpose"]',
          '[data-tour="seduyen-alias"]',
          '[data-tour="seduyen-avatar-switch"]',
          '[data-tour="seduyen-filters"]',
          '[data-tour="seduyen-location"]',
          '[data-tour="seduyen-goals"]',
          '[data-tour="seduyen-share-fields"]',
          '[data-tour="seduyen-submit"]',
        ];
      } else if (tourId === "seduyen-discover") {
        stepSelectors = [
          "body",
          '[data-tour="seduyen-intent-toggle"]',
          '[data-tour="seduyen-deck"]',
          '[data-tour="seduyen-search"]',
          '[data-tour="seduyen-invite"]',
          '[data-tour="seduyen-tabs"]',
        ];
      }

      const selector = stepSelectors[stepIndex];
      if (!selector || selector === "body") return;

      const container = document.querySelector("main");

      const isVisible = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };

      const pickVisibleElement = (sel: string): HTMLElement | null => {
        const elements = Array.from(document.querySelectorAll(sel));
        for (const el of elements) {
          if (el instanceof HTMLElement && isVisible(el)) {
            return el;
          }
        }
        const first = document.querySelector(sel);
        return first instanceof HTMLElement ? first : null;
      };

      const target = pickVisibleElement(selector);

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: Math.max(0, relativeTop - 24),
          behavior: "auto",
        });

        window.dispatchEvent(new Event("resize"));

        const interval = setInterval(() => {
          window.dispatchEvent(new Event("resize"));
        }, 50);
        setTimeout(() => {
          clearInterval(interval);
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    };

    const handleTourEvent = (e: Event) => {
      const { tourId, stepIndex, type } = (e as CustomEvent).detail || {};
      if (tourId !== "seduyen" && tourId !== "seduyen-discover") return;
      if (type === "step:after" || type === "tour:start" || type === "step:before") {
        scrollToStep(tourId, stepIndex);
      }
    };

    const handlePrescroll = (e: Event) => {
      const { tourId, stepIndex } = (e as CustomEvent).detail || {};
      if (tourId !== "seduyen" && tourId !== "seduyen-discover") return;
      scrollToStep(tourId, stepIndex);
    };

    window.addEventListener("tour-event", handleTourEvent);
    window.addEventListener("tour-prescroll", handlePrescroll);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
      window.removeEventListener("tour-prescroll", handlePrescroll);
    };
  }, [active]);

  // Tự chấp nhận lời mời từ URL ?invite=... (chạy 1 lần, kể cả khi chưa opt-in).
  const acceptedRef = useRef(false);
  const accept = useMutation({
    mutationFn: (code: string) => acceptMatchInvite(code),
    onSuccess: (res) => {
      toast.success(res.matched ? "Đã kết nối qua lời mời 💞" : "Đã gửi kết nối — chờ đối phương.");
      qc.invalidateQueries({ queryKey: ["mm-profile"] });
      qc.invalidateQueries({ queryKey: ["mm-connections"] });
      qc.invalidateQueries({ queryKey: ["mm-feed"] });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Lời mời không hợp lệ hoặc đã hết hạn"),
    onSettled: () => {
      // Bỏ ?invite khỏi URL để không xử lý lại khi quay lại trang.
      navigate({ to: "/se-duyen", search: {}, replace: true });
    },
  });
  useEffect(() => {
    if (invite && !acceptedRef.current) {
      acceptedRef.current = true;
      accept.mutate(invite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite]);

  return (
    <main className="app-feature-page app-feature-bg app-feature-bg--se-duyen flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-5xl px-3 py-6 pb-24 md:py-10">
        <header className="mb-6 text-center">
          <h1
            className="text-2xl font-bold text-gold-gradient md:text-3xl"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Se duyên & Hợp tác
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tương hợp Tử Vi, Bát Tự, vị trí, độ tuổi & lối sống — ẩn danh tới khi hai bên đồng ý.
          </p>
        </header>

        {profile.isLoading ? (
          <Skeleton className="h-40 w-full rounded-2xl" />
        ) : !active ? (
          <div className="max-w-4xl mx-auto w-full">
            <ProfileTab
              profile={profile.data ?? null}
              onlyOnboarding
              justActivatedRef={justActivatedProfileRef}
            />
          </div>
        ) : (
          <>
            <div data-tour="seduyen-intent-toggle" className="max-w-4xl mx-auto w-full">
              <IntentToggle intent={intent} onChange={setIntent} />
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <div className="max-w-4xl mx-auto w-full mb-4">
                <TabsList data-tour="seduyen-tabs" className="grid w-full grid-cols-3">
                  <TabsTrigger value="kham-pha" className="text-xs sm:text-sm px-1.5 sm:px-3">
                    <Sparkles className="mr-1 sm:mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Khám phá
                  </TabsTrigger>
                  <TabsTrigger value="ket-noi" className="text-xs sm:text-sm px-1.5 sm:px-3">
                    <Users className="mr-1 sm:mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Kết nối
                  </TabsTrigger>
                  <TabsTrigger value="ho-so" className="text-xs sm:text-sm px-1.5 sm:px-3">
                    <UserCog className="mr-1 sm:mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Hồ sơ
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="kham-pha" className="mt-4">
                <DiscoverTab intent={intent} activeTab={activeTab} />
              </TabsContent>
              <TabsContent value="ket-noi" className="mt-4">
                <div className="max-w-4xl mx-auto w-full">
                  <ConnectionsTab />
                </div>
              </TabsContent>
              <TabsContent value="ho-so" className="mt-4">
                <div className="max-w-4xl mx-auto w-full">
                  <ProfileTab
                    profile={profile.data ?? null}
                    justActivatedRef={justActivatedProfileRef}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </main>
  );
}

// ─── Intent toggle ────────────────────────────────────────────────────────────
function IntentToggle({
  intent,
  onChange,
}: {
  intent: MatchIntent;
  onChange: (i: MatchIntent) => void;
}) {
  return (
    <div className="flex gap-2">
      {INTENTS.map((it) => {
        const on = intent === it.key;
        return (
          <button
            key={it.key}
            onClick={() => onChange(it.key)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
              on
                ? "border-primary/60 bg-gold-gradient text-primary-foreground shadow-gold"
                : "border-border/40 bg-card/40 text-muted-foreground hover:text-foreground"
            }`}
          >
            <it.icon className="h-4 w-4" />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Khám phá: quẹt + tìm + mời ──────────────────────────────────────────────
function DiscoverTab({ intent, activeTab }: { intent: MatchIntent; activeTab: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const feed = useQuery({ queryKey: ["mm-feed", intent], queryFn: () => getMatchFeed(intent, 20) });
  const items = feed.data?.items ?? [];
  const top = items[0];

  // Ứng viên vừa quẹt (để Hoàn tác). Reset khi đổi mục đích.
  const [lastSwiped, setLastSwiped] = useState<MatchFeedItem | null>(null);
  useEffect(() => setLastSwiped(null), [intent]);

  const swipe = useMutation({
    mutationFn: (input: { item: MatchFeedItem; action: "like" | "pass" }) =>
      swipeMatch({ target_user_id: input.item.user_id, type: intent, action: input.action }),
    onSuccess: (res, vars) => {
      if (res.matched) {
        setLastSwiped(null); // đã match thì không cho hoàn tác để tránh huỷ kết nối nhầm
        toast.success("Tâm đầu ý hợp! Hai bạn đã kết nối 💞", {
          description: "Xem ở tab Kết nối để chọn thông tin chia sẻ và luận giải.",
        });
        qc.invalidateQueries({ queryKey: ["mm-connections"] });
      } else {
        setLastSwiped(vars.item);
      }
      qc.invalidateQueries({ queryKey: ["mm-feed", intent] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không thực hiện được"),
  });

  const undo = useMutation({
    mutationFn: () => undoSwipe({ target_user_id: lastSwiped!.user_id, type: intent }),
    onSuccess: () => {
      toast.success("Đã hoàn tác lượt quẹt");
      setLastSwiped(null);
      qc.invalidateQueries({ queryKey: ["mm-feed", intent] });
      qc.invalidateQueries({ queryKey: ["mm-connections"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không hoàn tác được"),
  });

  // Hỗ trợ phím tắt điều hướng (Desktop)
  useEffect(() => {
    if (activeTab !== "kham-pha" || !top) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") {
        e.preventDefault();
        swipe.mutate({ item: top, action: "pass" });
      } else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
        e.preventDefault();
        swipe.mutate({ item: top, action: "like" });
      } else if (e.key === "ArrowUp" || e.key === "w" || e.key === "W") {
        e.preventDefault();
        navigate({
          to: "/se-duyen/$handle",
          params: { handle: top.handle || String(top.user_id) },
          search: { type: intent },
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTab, top, intent, navigate, swipe]);

  return (
    <div className="max-w-md mx-auto w-full space-y-5">
      <Card data-tour="seduyen-deck" className="border-gold-soft/30 bg-card/60 shadow-mystic">
        <CardContent className="pt-6">
          {feed.isLoading ? (
            <Skeleton className="mx-auto h-[450px] w-full max-w-sm rounded-2xl animate-pulse" />
          ) : !top ? (
            <div className="py-24 text-center text-sm text-muted-foreground bg-background/20 rounded-2xl border border-dashed border-gold-soft/10">
              Hết người gợi ý cho mục này. Quay lại sau nhé!
            </div>
          ) : (
            <SwipeCard
              item={top}
              busy={swipe.isPending}
              onView={() =>
                navigate({
                  to: "/se-duyen/$handle",
                  params: { handle: top.handle || String(top.user_id) },
                  search: { type: intent },
                })
              }
              onLike={() => swipe.mutate({ item: top, action: "like" })}
              onPass={() => swipe.mutate({ item: top, action: "pass" })}
            />
          )}

          {/* Hoàn tác lượt quẹt */}
          {lastSwiped && !swipe.isPending ? (
            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => undo.mutate()}
                disabled={undo.isPending}
                className="text-muted-foreground hover:text-foreground"
              >
                {undo.isPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Undo2 className="mr-2 h-3.5 w-3.5" />
                )}
                Hoàn tác lượt vừa rồi
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function SwipeCard({
  item,
  onLike,
  onPass,
  onView,
  busy,
}: {
  item: MatchFeedItem;
  onLike: () => void;
  onPass: () => void;
  onView: () => void;
  busy: boolean;
}) {
  const alias = item.display_alias || "Ẩn danh";
  const photos = useMemo(() => {
    return [item.avatar, ...(item.photos ?? [])].filter(Boolean) as string[];
  }, [item]);
  const [currentIdx, setCurrentIdx] = useState(0);

  // Reset chỉ số ảnh khi đổi ứng viên
  useEffect(() => {
    setCurrentIdx(0);
  }, [item.user_id]);

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIdx((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIdx((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="mx-auto w-full max-w-sm">
      {/* Khung Thẻ quẹt */}
      <div className="relative w-full aspect-[3/4] overflow-hidden rounded-2xl border border-gold-soft/30 bg-background/50 shadow-mystic select-none group">
        {photos.length > 0 ? (
          <>
            <img
              src={photos[currentIdx]}
              alt={`${alias} - Ảnh ${currentIdx + 1}`}
              className="w-full h-full object-cover transition-all duration-300"
            />

            {/* Vùng click ẩn để chuyển ảnh nhanh */}
            <div className="absolute inset-0 flex z-10">
              <button
                type="button"
                onClick={handlePrevPhoto}
                className="w-1/3 h-full cursor-w-resize text-left outline-none"
                aria-label="Ảnh trước"
              />
              <button
                type="button"
                onClick={onView}
                className="w-1/3 h-full cursor-zoom-in outline-none"
                aria-label="Xem chi tiết"
              />
              <button
                type="button"
                onClick={handleNextPhoto}
                className="w-1/3 h-full cursor-e-resize text-right outline-none"
                aria-label="Ảnh sau"
              />
            </div>

            {/* Thanh biểu thị số lượng ảnh câu chuyện */}
            {photos.length > 1 && (
              <div className="absolute top-3 inset-x-3 flex gap-1 z-20 pointer-events-none">
                {photos.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      idx === currentIdx ? "bg-white shadow-xs" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/60 p-6 text-center">
            <ViewableAvatar
              src={item.avatar}
              alias={alias}
              className="h-24 w-24 border-2 border-gold-soft/50 shadow-md"
              fallbackClassName="bg-gold-gradient text-3xl text-primary-foreground"
            />
            <div className="mt-4 text-lg font-bold text-foreground">{alias}</div>
          </div>
        )}

        {/* Lớp phủ gradient mờ đẹp ở chân thẻ */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent pointer-events-none z-10" />

        {/* Panel thông tin của ứng viên */}
        <div className="absolute bottom-0 inset-x-0 p-4 text-left z-20 pointer-events-none flex flex-col gap-1.5">
          <div>
            <div className="flex items-baseline gap-2">
              <span
                className="text-xl font-bold text-white tracking-wide"
                style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
              >
                {alias}
              </span>
              {item.age && (
                <span
                  className="text-lg font-semibold text-white/90"
                  style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
                >
                  {item.age}
                </span>
              )}
            </div>
            <div
              className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-white/85"
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
            >
              {item.gender && (
                <span>
                  {item.gender === "male" ? "Nam" : item.gender === "female" ? "Nữ" : "Khác"}
                </span>
              )}
              {item.chi_nam && <span>· {item.chi_nam}</span>}
              {item.province && (
                <span className="flex items-center gap-0.5 ml-1">
                  <MapPin className="h-3.5 w-3.5 text-[#E5B869]" />
                  {item.province}
                </span>
              )}
              {item.distance_label && (
                <span className="text-[#E5B869] font-medium">· {item.distance_label}</span>
              )}
            </div>
          </div>

          {/* Huy hiệu Độ tương hợp */}
          <div className="flex flex-wrap items-center gap-1.5">
            <div
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold backdrop-blur-md shadow-xs ${bandColor(item.band?.key)}`}
            >
              <Sparkles className="h-3 w-3 animate-pulse" />
              Hợp {item.prelim_score}/100 · {item.band?.label}
            </div>
          </div>

          {/* Lý do kết nối */}
          {item.match_reasons && item.match_reasons.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.match_reasons.slice(0, 3).map((r, i) => (
                <span
                  key={i}
                  className="rounded-full bg-white/10 border border-white/10 px-2 py-0.5 text-[10px] text-white/90 backdrop-blur-xs"
                >
                  {r}
                </span>
              ))}
            </div>
          )}

          {/* Nhận xét tương hợp */}
          {item.band?.description && (
            <p className="text-[11px] leading-snug text-white/70 line-clamp-2 mt-1 pointer-events-none">
              {item.band.description}
            </p>
          )}
        </div>

        {/* Nút xem chi tiết (Info) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          className="absolute bottom-4 right-4 z-35 p-2 rounded-full bg-black/40 text-white backdrop-blur-md border border-white/20 hover:bg-black/60 transition cursor-pointer"
          title="Xem hồ sơ đầy đủ"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      {/* Các nút quẹt */}
      <div className="mt-5 flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="lg"
          disabled={busy}
          onClick={onPass}
          className="h-14 w-14 rounded-full border-border/60 p-0 shadow-sm transition hover:scale-110 hover:border-rose-500/60 hover:text-rose-400 hover:bg-rose-500/5 active:scale-95"
        >
          <X className="h-6 w-6" />
        </Button>
        <Button
          size="lg"
          disabled={busy}
          onClick={onLike}
          className="h-16 w-16 rounded-full bg-gold-gradient p-0 text-primary-foreground shadow-gold transition hover:scale-110 hover:brightness-110 active:scale-95"
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Heart className="h-7 w-7" />}
        </Button>
      </div>
    </div>
  );
}

// ─── Kết nối ──────────────────────────────────────────────────────────────────
const CONN_FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Mọi trạng thái" },
  { key: "connected", label: "Đã kết nối" },
  { key: "pending", label: "Chờ" },
  { key: "passed", label: "Đã bỏ qua" },
];

const CONN_TYPE_FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Tất cả loại" },
  { key: "se_duyen", label: "Se duyên" },
  { key: "lam_an", label: "Hợp tác làm ăn" },
];

function ConnectionsTab() {
  const conns = useQuery({ queryKey: ["mm-connections"], queryFn: () => listMatchConnections() });
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  useEffect(() => {
    setPage(1);
  }, [filter, typeFilter]);

  const all = conns.data ?? [];
  const data = all.filter(
    (c) =>
      (filter === "all" || c.status === filter) && (typeFilter === "all" || c.type === typeFilter),
  );

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const paginatedData = data.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-primary" /> Kết nối của bạn
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Lọc gọn: loại kết nối + trạng thái trên cùng 1 hàng */}
        <div className="mb-3 flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 flex-1 rounded-full border-border/40 bg-background/40 px-3 text-xs">
              <span className="inline-flex items-center gap-1.5 truncate">
                {typeFilter === "se_duyen" ? (
                  <Heart className="h-3 w-3 shrink-0 text-primary" />
                ) : typeFilter === "lam_an" ? (
                  <HeartHandshake className="h-3 w-3 shrink-0 text-primary" />
                ) : null}
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent>
              {CONN_TYPE_FILTERS.map((f) => (
                <SelectItem key={f.key} value={f.key} className="text-xs">
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 flex-1 rounded-full border-border/40 bg-background/40 px-3 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONN_FILTERS.map((f) => (
                <SelectItem key={f.key} value={f.key} className="text-xs">
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {conns.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Chưa có kết nối nào. Hãy quẹt ở tab Khám phá để tìm hữu duyên!
          </p>
        ) : (
          <ul className="space-y-2">
            {paginatedData.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => setOpenId(c.id)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border/40 bg-background/40 px-3 py-2.5 text-left transition hover:border-primary/40"
                >
                  <Avatar className="h-9 w-9">
                    {c.other_avatar ? (
                      <AvatarImage src={c.other_avatar} alt={c.other_alias || ""} />
                    ) : null}
                    <AvatarFallback className="bg-gold-gradient text-xs text-primary-foreground">
                      {(c.other_alias || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{c.other_alias || "Ẩn danh"}</div>
                    <div className="text-xs text-muted-foreground">
                      {c.type === "se_duyen" ? "Se duyên" : "Hợp tác"} ·{" "}
                      {c.prelim_score != null ? `Hợp ${c.prelim_score}` : "—"}
                    </div>
                  </div>
                  <ConnBadge c={c} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Cụm điều khiển phân trang (chỉ hiện khi có từ 2 trang trở lên) */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-border/30 pt-3">
            <span className="text-[11px] sm:text-xs text-muted-foreground select-none">
              Hiển thị {Math.min(data.length, (page - 1) * ITEMS_PER_PAGE + 1)}-
              {Math.min(data.length, page * ITEMS_PER_PAGE)} trong {data.length} kết nối
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 px-2.5 text-xs cursor-pointer select-none"
              >
                <ChevronLeft className="mr-0.5 h-3.5 w-3.5" /> Trước
              </Button>
              <span className="text-xs font-medium text-foreground select-none px-1.5 sm:px-2">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8 px-2.5 text-xs cursor-pointer select-none"
              >
                Sau <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {openId ? (
        <ConnectionDialog id={openId} open={!!openId} onClose={() => setOpenId(null)} />
      ) : null}
    </Card>
  );
}

function ConnBadge({ c }: { c: MatchConnectionSummary }) {
  if (c.status === "connected")
    return <Badge className="bg-emerald-500/20 text-emerald-300">Đã kết nối</Badge>;
  if (c.status === "passed")
    return (
      <Badge variant="outline" className="text-muted-foreground">
        Đã bỏ qua
      </Badge>
    );
  // pending: phân biệt họ thích mình vs mình chờ họ.
  if (c.they_liked && !c.i_liked)
    return <Badge className="bg-pink-500/20 text-pink-300">💛 Đã thích bạn</Badge>;
  return <Badge className="bg-amber-500/20 text-amber-300">Chờ đối phương</Badge>;
}

function ConnectionDialog({
  id,
  open,
  onClose,
}: {
  id: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { refresh } = useBoideUser();
  const { confirm, confirmDialog } = useConfirm();
  const detail = useQuery({ queryKey: ["mm-conn", id], queryFn: () => getMatchConnection(id) });
  const reading = useQuery({ queryKey: ["mm-reading", id], queryFn: () => getCompatReading(id) });
  const d = detail.data;
  const connected = d?.status === "connected";

  const cast = useMutation({
    mutationFn: (force: boolean = false) => castCompatReading(id, force),
    onSuccess: (res) => {
      if (res.price_charged > 0) {
        toast.success(`Đã luận giải · trừ ${res.price_charged.toLocaleString("vi-VN")}đ`);
        refresh();
      } else {
        toast.success("Đã cập nhật luận giải");
      }
      qc.setQueryData(["mm-reading", id], res);
      qc.invalidateQueries({ queryKey: ["mm-connections"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Luận giải thất bại"),
  });

  // Kết nối lại sau khi lỡ bỏ qua: gửi "like" lại (ghi đè 'pass').
  const reconnect = useMutation({
    mutationFn: () =>
      swipeMatch({
        target_user_id: d!.other_user_id,
        type: d!.type as MatchIntent,
        action: "like",
      }),
    onSuccess: (res) => {
      toast.success(res.matched ? "Đã kết nối lại 💞" : "Đã gửi lời thích lại — chờ đối phương.");
      qc.invalidateQueries({ queryKey: ["mm-conn", id] });
      qc.invalidateQueries({ queryKey: ["mm-connections"] });
      qc.invalidateQueries({ queryKey: ["mm-feed"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không kết nối lại được"),
  });

  // Huỷ kết nối: đưa kết nối về 'passed'.
  const disconnect = useMutation({
    mutationFn: () => disconnectMatchConnection(id),
    onSuccess: () => {
      toast.success("Đã huỷ kết nối");
      qc.invalidateQueries({ queryKey: ["mm-conn", id] });
      qc.invalidateQueries({ queryKey: ["mm-connections"] });
      qc.invalidateQueries({ queryKey: ["mm-feed"] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không huỷ kết nối được"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg w-[calc(100%-2rem)] max-w-full overflow-x-hidden min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-gold-gradient">
            <ViewableAvatar
              src={d?.other_avatar}
              alias={d?.other_alias}
              className="h-10 w-10 border border-gold-soft/50"
              fallbackClassName="bg-gold-gradient text-sm text-primary-foreground"
            />
            <span className="flex items-center gap-2">
              <HeartHandshake className="h-5 w-5" />
              {d?.other_alias || "Kết nối"}
            </span>
          </DialogTitle>
        </DialogHeader>

        {detail.isLoading || !d ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : d.status === "passed" ? (
          <div className="space-y-4 py-4 text-center">
            <p className="text-sm text-muted-foreground">
              Kết nối này đã bị bỏ qua. Nếu lỡ tay, bạn có thể thích lại để nối lại duyên.
            </p>
            <Button
              onClick={() => reconnect.mutate()}
              disabled={reconnect.isPending}
              className="bg-gold-gradient text-primary-foreground shadow-gold hover:brightness-110"
            >
              {reconnect.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Heart className="mr-2 h-4 w-4" />
              )}
              Kết nối lại
            </Button>
          </div>
        ) : !connected ? (
          d.they_liked && !d.i_liked ? (
            <div className="space-y-4 py-4 text-center">
              <p className="text-sm text-pink-300">
                💛 {d.other_alias || "Đối phương"} đã thích bạn! Thích lại để kết nối ngay.
              </p>
              <Button
                onClick={() => reconnect.mutate()}
                disabled={reconnect.isPending}
                className="bg-gold-gradient text-primary-foreground shadow-gold hover:brightness-110"
              >
                {reconnect.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Heart className="mr-2 h-4 w-4" />
                )}
                Thích lại để kết nối
              </Button>
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Đã gửi lời thích. Đang chờ đối phương đồng ý — khi họ thích lại, hai bạn sẽ kết nối.
            </p>
          )
        ) : (
          <div className="space-y-4">
            {d.prelim_score != null ? (
              <div className="rounded-xl border border-primary/30 bg-gold-gradient/10 px-4 py-2.5 text-center text-sm">
                Điểm hợp sơ bộ:{" "}
                <span className="font-semibold text-primary">{d.prelim_score}/100</span>
              </div>
            ) : null}

            {d.other_profile ? (
              <FullProfileBody profile={d.other_profile} allowReveal={true} />
            ) : null}
            <Separator />
            <ReadingSection
              id={id}
              md={reading.data?.reading_md ?? null}
              loading={reading.isLoading}
              casting={cast.isPending}
              onCast={() => cast.mutate(false)}
              onRecast={() => cast.mutate(true)}
            />
            <Separator />
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                disabled={disconnect.isPending}
                onClick={async () => {
                  // Huỷ kết nối là mất luôn bài luận giải tương hợp ĐÃ TRẢ TIỀN,
                  // không chỉ mất đường nhắn tin.
                  const ok = await confirm({
                    title: "Huỷ kết nối với người này?",
                    description: (
                      <>
                        Bài luận giải tương hợp đã trả tiền sẽ bị xoá và bạn cần thích lại từ đầu
                        nếu muốn nối lại. Hành động này không thể hoàn tác.
                      </>
                    ),
                    confirmLabel: "Huỷ kết nối",
                  });
                  if (ok) disconnect.mutate();
                }}
                className="text-destructive/80 hover:bg-destructive/10 hover:text-destructive"
              >
                {disconnect.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <HeartCrack className="mr-2 h-4 w-4" />
                )}
                Huỷ kết nối
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      {confirmDialog}
    </Dialog>
  );
}

function SocialLinksValue({ value }: { value: Record<string, string> }) {
  const SOCIAL_LABELS: Record<string, string> = {
    facebook: "Facebook",
    zalo: "Zalo",
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
  };
  const entries = Object.entries(value || {}).filter(([, v]) => v && String(v).trim());
  if (entries.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {entries.map(([k, v]) => {
        const isUrl = /^https?:\/\//i.test(v);
        return isUrl ? (
          <a
            key={k}
            href={v}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:brightness-110"
          >
            {SOCIAL_LABELS[k] || k}
          </a>
        ) : (
          <span key={k}>
            {SOCIAL_LABELS[k] || k}: {v}
          </span>
        );
      })}
    </div>
  );
}

function ReadingSection({
  md,
  loading,
  casting,
  onCast,
  onRecast,
}: {
  id: string;
  md: string | null;
  loading: boolean;
  casting: boolean;
  onCast: () => void;
  onRecast: () => void;
}) {
  if (loading) return <Skeleton className="h-24 w-full rounded-xl" />;
  if (md) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-gold-soft/30 bg-background/30 p-4 text-sm leading-relaxed text-foreground/90">
          <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS} components={MD_COMPONENTS}>
            {md}
          </ReactMarkdown>
          <AiWatermark />
        </div>
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onRecast}
            disabled={casting}
            className="border-border/60 text-muted-foreground hover:text-foreground"
          >
            {casting ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
            )}
            Luận giải lại
          </Button>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Tính lại theo thông tin & lá số mới nhất (có thể trừ 10.000đ).
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="text-center">
      <Button
        onClick={onCast}
        disabled={casting}
        className="bg-gold-gradient text-primary-foreground shadow-gold hover:brightness-110"
      >
        {casting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        Xem luận giải tương hợp
      </Button>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Lần luận giải đầu tiên miễn phí, sau đó 10.000đ mỗi kết nối.
      </p>
    </div>
  );
}

const MAX_GALLERY_PHOTOS = 9;

function PhotoGallery({
  photos: initialPhotos,
  disabled,
  dataTour,
}: {
  photos: string[];
  disabled?: boolean;
  dataTour?: string;
}) {
  const [photos, setPhotos] = useState<string[]>(initialPhotos);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setPhotos(initialPhotos), [initialPhotos]);

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    const room = MAX_GALLERY_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Tối đa ${MAX_GALLERY_PHOTOS} ảnh`);
      return;
    }
    const picked = files.slice(0, room);
    setBusy(true);
    try {
      const res = await uploadMatchPhotos(picked);
      setPhotos(res.photos);
      toast.success("Đã thêm ảnh");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tải ảnh thất bại");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (index: number) => {
    setBusy(true);
    try {
      const res = await deleteMatchPhoto(index);
      setPhotos(res.photos);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Xoá ảnh thất bại");
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= photos.length) return;
    const next = [...photos];
    [next[index], next[target]] = [next[target], next[index]];
    setPhotos(next); // optimistic
    setBusy(true);
    try {
      const res = await reorderMatchPhotos(next);
      setPhotos(res.photos);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sắp xếp thất bại");
      setPhotos(photos); // rollback
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-tour={dataTour}
      className="space-y-2 rounded-xl border border-border/40 bg-background/40 px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-foreground">
            Album ảnh ({photos.length}/{MAX_GALLERY_PHOTOS})
          </div>
          <div className="text-xs text-muted-foreground">
            {disabled
              ? "Lưu hồ sơ se duyên trước, sau đó quay lại để thêm ảnh."
              : "Thêm tối đa 9 ảnh ngoài ảnh đại diện. Theo cùng quy tắc hiển thị ở trên."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {photos.map((url, i) => (
          <div
            key={url + i}
            className="group relative aspect-square overflow-hidden rounded-lg border border-border/40"
          >
            <img src={url} alt={`Ảnh ${i + 1}`} className="h-full w-full object-cover" />
            <button
              type="button"
              disabled={busy}
              onClick={() => remove(i)}
              className="absolute right-1.5 top-1.5 rounded-full bg-black/65 p-1.5 text-white transition lg:opacity-0 lg:group-hover:opacity-100 opacity-100 z-10 hover:bg-red-500/80 active:scale-95"
              aria-label="Xoá ảnh"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 px-2 py-1 transition lg:opacity-0 lg:group-hover:opacity-100 opacity-100">
              <button
                type="button"
                disabled={busy || i === 0}
                onClick={() => move(i, -1)}
                className="text-white disabled:opacity-30 p-0.5 hover:text-primary active:scale-90"
                aria-label="Lên trước"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={busy || i === photos.length - 1}
                onClick={() => move(i, 1)}
                className="text-white disabled:opacity-30 p-0.5 hover:text-primary active:scale-90"
                aria-label="Xuống sau"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {photos.length < MAX_GALLERY_PHOTOS && !disabled ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border/50 text-muted-foreground transition hover:border-primary/50 hover:text-primary"
          >
            <ImagePlus className="h-5 w-5" />
            <span className="text-[11px]">Thêm ảnh</span>
          </button>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPickFiles}
      />
    </div>
  );
}

const SOCIAL_FIELDS: { key: keyof SocialLinks; label: string }[] = [
  { key: "facebook", label: "Facebook" },
  { key: "zalo", label: "Zalo" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
];

const GENDER_LABELS: Record<string, string> = { male: "Nam", female: "Nữ", other: "Khác" };

/** Ô hiển thị chỉ-đọc một trường hồ sơ cá nhân (sửa trong dialog Tài khoản). */
function PersonalValue({ label, value }: { label?: string; value?: string | null }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm">
      {label ? <div className="text-[11px] text-muted-foreground">{label}</div> : null}
      <div className={value ? "text-foreground" : "italic text-muted-foreground"}>
        {value || "Chưa cập nhật"}
      </div>
    </div>
  );
}

function FieldLabel({
  text,
  field,
  visibility,
  onToggleVisibility,
}: {
  text: string;
  field: ShareableField;
  visibility: FieldVisibilityMap;
  onToggleVisibility: (f: ShareableField) => void;
}) {
  const isDefaultHidden = ["birth_time", "email", "phone"].includes(field);
  const visible = visibility[field] === undefined ? !isDefaultHidden : visibility[field] === true;
  return (
    <div className="mb-1.5 flex items-center justify-between">
      <Label className="block text-sm">{text}</Label>
      <VisibilityToggle visible={visible} onToggle={() => onToggleVisibility(field)} label={text} />
    </div>
  );
}

function ProfileTab({
  profile,
  onlyOnboarding,
  justActivatedRef,
}: {
  profile: import("@/lib/boide-api").MatchProfile | null;
  onlyOnboarding?: boolean;
  justActivatedRef?: React.MutableRefObject<boolean>;
}) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me-profile"], queryFn: getProfile });

  const [intents, setIntents] = useState<MatchIntent[]>(profile?.intents ?? ["se_duyen"]);
  const [alias, setAlias] = useState(profile?.display_alias ?? "");
  const [handle, setHandle] = useState<string>(profile?.handle ?? "");
  const [handleError, setHandleError] = useState<string | null>(null);
  const [prefGender, setPrefGender] = useState<string>(profile?.pref_gender ?? "");
  const [prefGenderLamAn, setPrefGenderLamAn] = useState<string>(profile?.pref_gender_lam_an ?? "");
  const [showAvatar, setShowAvatar] = useState<boolean>(profile?.show_avatar ?? true);
  const [visibility, setVisibility] = useState<FieldVisibilityMap>(profile?.field_visibility ?? {});
  const toggleVisible = (f: ShareableField) =>
    setVisibility((p) => {
      const isDefaultHidden = ["birth_time", "email", "phone"].includes(f);
      const currentValue = p[f];
      let newValue: boolean;
      if (currentValue === undefined) {
        newValue = isDefaultHidden ? true : false;
      } else {
        newValue = !currentValue;
      }
      return { ...p, [f]: newValue };
    });

  const [location, setLocation] = useState<LocationValue>({
    latitude: profile?.latitude ?? null,
    longitude: profile?.longitude ?? null,
    province_code: profile?.province_code ?? null,
    region: profile?.region ?? null,
    location_label: profile?.location_label ?? null,
    legacy_location: profile?.legacy_location ?? null,
  });
  const [prefAgeMin, setPrefAgeMin] = useState<string>(
    profile?.pref_age_min != null ? String(profile.pref_age_min) : "",
  );
  const [prefAgeMax, setPrefAgeMax] = useState<string>(
    profile?.pref_age_max != null ? String(profile.pref_age_max) : "",
  );
  const [prefAgeMinLamAn, setPrefAgeMinLamAn] = useState<string>(
    profile?.pref_age_min_lam_an != null ? String(profile.pref_age_min_lam_an) : "",
  );
  const [prefAgeMaxLamAn, setPrefAgeMaxLamAn] = useState<string>(
    profile?.pref_age_max_lam_an != null ? String(profile.pref_age_max_lam_an) : "",
  );
  const [goalTags, setGoalTags] = useState<GoalTagsMap>(profile?.goal_tags ?? {});
  const [occupation, setOccupation] = useState<string>(profile?.occupation ?? "");
  const [hobbiesText, setHobbiesText] = useState<string>((profile?.hobbies ?? []).join(", "));
  const meta = useQuery({
    queryKey: ["mm-meta"],
    queryFn: getMatchMeta,
    staleTime: 60 * 60 * 1000,
  });

  // ── Hồ sơ cá nhân (gộp thẳng vào đây thay vì dialog riêng) ──
  // Chỉ đọc ở đây — sửa qua dialog Tài khoản, lưu độc lập với hồ sơ se duyên.
  const personal: Partial<BoideProfile> = me.data ?? {};
  const [personalDialogOpen, setPersonalDialogOpen] = useState(false);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onPickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Vui lòng chọn file ảnh");
      return;
    }
    setPendingAvatarFile(file);
    setCropOpen(true);
  };
  const onConfirmCroppedAvatar = async (croppedFile: File) => {
    setUploadingAvatar(true);
    try {
      await uploadAvatar(croppedFile);
      await qc.invalidateQueries({ queryKey: ["me-profile"] });
      await refreshBoideUser();
      toast.success("Đã cập nhật ảnh đại diện");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tải ảnh thất bại");
    } finally {
      setUploadingAvatar(false);
      setPendingAvatarFile(null);
    }
  };

  const toggleGoal = (intent: MatchIntent, tag: string) =>
    setGoalTags((prev) => {
      const cur = prev[intent] ?? [];
      const next = cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag];
      return { ...prev, [intent]: next };
    });

  // ── Bắt buộc để tính điểm tương hợp (vẫn cho phép ẩn khỏi hiển thị công khai) ──
  const hobbiesList = useMemo(
    () =>
      Array.from(
        new Set(
          hobbiesText
            .split(",")
            .map((h) => h.trim())
            .filter(Boolean),
        ),
      ),
    [hobbiesText],
  );
  const hasLocation = !!(location.province_code || (location.latitude && location.longitude));
  const missingByIntent: Record<string, string[]> = {};
  for (const it of intents) {
    if (!(goalTags[it] ?? []).length) {
      missingByIntent[it] = [
        ...(missingByIntent[it] ?? []),
        it === "lam_an" ? "mục tiêu/tầm nhìn (làm ăn)" : "mục tiêu sống chung (se duyên)",
      ];
    }
  }
  const validationErrors: string[] = [];
  if (intents.length === 0) {
    validationErrors.push(
      onlyOnboarding
        ? "Cần chọn ít nhất 1 mục đích kết nối (Se duyên hoặc Hợp tác làm ăn)"
        : "Cần chọn ít nhất 1 mục đích kết nối. Để ẩn hồ sơ, hãy dùng tính năng 'Tạm ẩn hồ sơ' ở phía dưới.",
    );
  }
  if (hobbiesList.length === 0) validationErrors.push("Cần điền ít nhất 1 sở thích");
  if (!hasLocation) validationErrors.push("Cần chọn vị trí (tỉnh/thành)");
  Object.values(missingByIntent)
    .flat()
    .forEach((m) => validationErrors.push(`Cần chọn ít nhất 1 ${m}`));
  if (intents.includes("lam_an") && !occupation.trim())
    validationErrors.push("Cần nhập nghề nghiệp (bắt buộc cho hợp tác làm ăn)");
  if (handleError) validationErrors.push(handleError);
  // Tương hợp Tử Vi/Bát Tự & lọc giới cần ngày sinh + giới tính từ hồ sơ cá nhân.
  const missingPersonal = [
    !personal.birth_date && "ngày sinh",
    !personal.gender && "giới tính",
  ].filter(Boolean);
  if (me.data && missingPersonal.length > 0)
    validationErrors.push(
      `Cần cập nhật ${missingPersonal.join(", ")} — bấm "Sửa hồ sơ cá nhân" ở trên`,
    );

  const ageRangeErrors: string[] = [];
  if (prefAgeMin && prefAgeMax && Number(prefAgeMin) > Number(prefAgeMax))
    ageRangeErrors.push("Độ tuổi se duyên: 'Từ' phải nhỏ hơn hoặc bằng 'Đến'");
  if (prefAgeMinLamAn && prefAgeMaxLamAn && Number(prefAgeMinLamAn) > Number(prefAgeMaxLamAn))
    ageRangeErrors.push("Độ tuổi làm ăn: 'Từ' phải nhỏ hơn hoặc bằng 'Đến'");

  const deactivate = useMutation({
    mutationFn: deactivateMatchProfile,
    onSuccess: () => {
      toast.success("Đã tạm ẩn hồ sơ se duyên thành công 🔏");
      qc.invalidateQueries({ queryKey: ["mm-profile"] });
      qc.invalidateQueries({ queryKey: ["me-profile"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Tạm ẩn hồ sơ thất bại"),
  });

  const save = useMutation({
    mutationFn: () =>
      upsertMatchProfile({
        intents,
        display_alias: alias || undefined,
        pref_gender: prefGender || null,
        pref_gender_lam_an: prefGenderLamAn || null,
        pref_age_min: prefAgeMin ? Number(prefAgeMin) : null,
        pref_age_max: prefAgeMax ? Number(prefAgeMax) : null,
        pref_age_min_lam_an: prefAgeMinLamAn ? Number(prefAgeMinLamAn) : null,
        pref_age_max_lam_an: prefAgeMaxLamAn ? Number(prefAgeMaxLamAn) : null,
        field_visibility: visibility,
        show_avatar: showAvatar,
        province_code: location.province_code ?? null,
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
        location_label: location.location_label ?? null,
        legacy_location: location.legacy_location ?? null,
        goal_tags: goalTags,
        occupation: occupation.trim() || null,
        hobbies: hobbiesList,
        handle: handle.trim() || null,
      }),
    onSuccess: () => {
      toast.success(onlyOnboarding ? "Đã bật hồ sơ se duyên 🌸" : "Đã cập nhật hồ sơ");
      if (onlyOnboarding && justActivatedRef) {
        justActivatedRef.current = true;
      }
      setSavedKey(currentKeyRef.current); // đánh dấu đã lưu → nút mờ
      qc.invalidateQueries({ queryKey: ["mm-profile"] });
      qc.invalidateQueries({ queryKey: ["mm-feed"] });
      qc.invalidateQueries({ queryKey: ["me-profile"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Lưu hồ sơ thất bại"),
  });

  // ── Trạng thái "đã chỉnh sửa" (dirty) để bật/mờ nút Lưu ──
  const currentKey = useMemo(
    () =>
      JSON.stringify({
        intents: [...intents].sort(),
        alias: alias.trim(),
        prefGender,
        prefGenderLamAn,
        showAvatar,
        visibility,
        location: {
          lat: location.latitude ?? null,
          lon: location.longitude ?? null,
          prov: location.province_code ?? null,
          label: location.location_label ?? null,
          legacy: location.legacy_location ?? null,
        },
        age: [prefAgeMin, prefAgeMax, prefAgeMinLamAn, prefAgeMaxLamAn],
        goals: {
          se_duyen: [...(goalTags.se_duyen ?? [])].sort(),
          lam_an: [...(goalTags.lam_an ?? [])].sort(),
        },
        occupation: occupation.trim(),
        hobbiesText: hobbiesText.trim(),
        handle: handle.trim(),
      }),
    [
      intents,
      alias,
      prefGender,
      prefGenderLamAn,
      showAvatar,
      visibility,
      location,
      prefAgeMin,
      prefAgeMax,
      prefAgeMinLamAn,
      prefAgeMaxLamAn,
      goalTags,
      occupation,
      hobbiesText,
      handle,
    ],
  );
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;
  // null = chưa có mốc đã lưu (onboarding) → coi như dirty để cho phép bật hồ sơ.
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    if (profile) setSavedKey(currentKeyRef.current); // hồ sơ sẵn có → bắt đầu ở trạng thái "đã lưu"
  }, [profile]);
  const dirty = savedKey !== currentKey;

  const toggleIntent = (k: MatchIntent) =>
    setIntents((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Heart className="h-4 w-4 text-primary" />
          {onlyOnboarding ? "Bật hồ sơ se duyên" : "Hồ sơ se duyên của bạn"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {onlyOnboarding ? (
          <p className="text-sm text-muted-foreground">
            Tham gia để được gợi ý những người hữu duyên dựa trên tương hợp Tử Vi, Bát Tự, vị trí,
            độ tuổi & mục tiêu sống. Mỗi mục bạn điền có nút mắt riêng để bật/tắt hiển thị công khai
            — người khác xem được ngay khi click vào hồ sơ bạn trên feed.
          </p>
        ) : null}

        {/* Ảnh đại diện */}
        <div
          data-tour={onlyOnboarding ? "seduyen-personal-info" : undefined}
          className="flex items-center gap-4"
        >
          <Avatar className="h-20 w-20 border-2 border-gold-soft/50">
            {personal.avatar ? <AvatarImage src={personal.avatar} alt="avatar" /> : null}
            <AvatarFallback className="bg-gold-gradient text-xl text-primary-foreground">
              {(personal.real_name || alias || "?").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickAvatar}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploadingAvatar}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadingAvatar ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Camera className="mr-2 h-4 w-4" />
              )}
              Đổi ảnh đại diện
            </Button>
            <p className="mt-1 text-[11px] text-muted-foreground">JPG/PNG, tối đa ~5MB.</p>
          </div>
        </div>

        <div data-tour={onlyOnboarding ? "seduyen-purpose" : undefined}>
          <Label className="mb-2 block text-sm">Mục đích kết nối</Label>
          <div className="flex gap-2">
            {INTENTS.map((it) => {
              const on = intents.includes(it.key);
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => toggleIntent(it.key)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                    on
                      ? "border-primary/60 bg-gold-gradient/15 text-foreground"
                      : "border-border/40 bg-background/40 text-muted-foreground"
                  }`}
                >
                  <it.icon className="h-4 w-4" /> {it.label}
                </button>
              );
            })}
          </div>
        </div>

        <div data-tour={onlyOnboarding ? "seduyen-alias" : undefined}>
          <Label htmlFor="alias" className="mb-2 block text-sm">
            Biệt danh (luôn hiển thị, kể cả khi ẩn hết các mục khác)
          </Label>
          <Input
            id="alias"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="VD: Hữu Duyên"
            maxLength={40}
          />
        </div>

        <div>
          <Label htmlFor="handle" className="mb-2 block text-sm">
            URL hồ sơ (tuỳ chỉnh)
          </Label>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span className="shrink-0">/se-duyen/</span>
            <Input
              id="handle"
              value={handle}
              onChange={(e) => {
                const v = e.target.value.trim().toLowerCase();
                setHandle(v);
                if (!v) {
                  setHandleError(null);
                } else if (!/^[a-z0-9_-]{3,30}$/.test(v) || /^[0-9]+$/.test(v)) {
                  setHandleError(
                    "Handle chỉ gồm chữ thường/số/gạch dưới/gạch ngang, 3-30 ký tự, không toàn số",
                  );
                } else {
                  setHandleError(null);
                }
              }}
              placeholder={profile?.user_id ? String(profile.user_id) : "vd: huu-duyen"}
              maxLength={30}
              className="flex-1"
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Để trống = dùng ID mặc định ({profile?.user_id ?? "..."}).
            {handleError ? <span className="ml-1 text-rose-400">{handleError}</span> : null}
          </p>
        </div>

        <div
          data-tour={onlyOnboarding ? "seduyen-avatar-switch" : undefined}
          className="flex items-center justify-between rounded-xl border border-border/40 bg-background/40 px-4 py-3"
        >
          <div className="text-sm">
            <div className="font-medium text-foreground">Hiển thị ảnh đại diện</div>
            <div className="text-xs text-muted-foreground">
              Tắt nếu muốn ẩn danh hoàn toàn — người khác chỉ thấy biệt danh.
            </div>
          </div>
          <Switch checked={showAvatar} onCheckedChange={setShowAvatar} />
        </div>

        <PhotoGallery
          photos={profile?.photos ?? []}
          dataTour={onlyOnboarding ? "seduyen-gallery" : undefined}
          disabled={!profile}
        />

        {/* Hồ sơ cá nhân — chỉ hiển thị + nút mắt bật/tắt công khai; sửa trong dialog Tài khoản
            (tách khỏi se duyên để user không dùng se duyên vẫn sửa hồ sơ được). */}
        <div className="space-y-3 rounded-xl border border-border/40 bg-background/30 p-3">
          <div className="flex items-center justify-between gap-2">
            <Label className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <UserCog className="h-3.5 w-3.5 text-primary" /> Hồ sơ cá nhân
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPersonalDialogOpen(true)}
            >
              <Pencil className="mr-2 h-3.5 w-3.5" /> Sửa hồ sơ cá nhân
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel
                text="Họ tên thật"
                field="real_name"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <PersonalValue value={personal.real_name} />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Giới tính</Label>
              <PersonalValue value={personal.gender ? GENDER_LABELS[personal.gender] : null} />
            </div>
            <div>
              <FieldLabel
                text="Ngày sinh"
                field="birth_date"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <PersonalValue
                value={
                  personal.birth_date
                    ? `${personal.birth_date} (${personal.birth_calendar === "lunar" ? "âm lịch" : "dương lịch"})`
                    : null
                }
              />
            </div>
            <div>
              <FieldLabel
                text="Giờ sinh"
                field="birth_time"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <PersonalValue value={personal.birth_time} />
            </div>
            <div>
              <FieldLabel
                text="Nơi ở hiện tại"
                field="current_location"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <PersonalValue value={personal.current_location} />
            </div>
            <div>
              <FieldLabel
                text="Số điện thoại"
                field="phone"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <PersonalValue value={personal.phone} />
            </div>
          </div>
        </div>

        {/* Gom các phần chi tiết vào accordion để gọn (mở sẵn khi onboarding) */}
        <Accordion
          type="multiple"
          defaultValue={onlyOnboarding ? ["boloc", "vitri", "muctieu", "sothich"] : []}
          className="space-y-2"
        >
          <AccordionItem
            value="boloc"
            data-tour={onlyOnboarding ? "seduyen-filters" : undefined}
            className="overflow-hidden rounded-xl border border-border/40 bg-background/30 px-3"
          >
            <AccordionTrigger className="py-3 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-primary" /> Bộ lọc đối phương
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              {intents.includes("se_duyen") ? (
                <div>
                  <Label className="mb-2 block text-sm">Ưu tiên giới tính (se duyên)</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "", l: "Mặc định (khác giới)" },
                      { v: "male", l: "Nam" },
                      { v: "female", l: "Nữ" },
                      { v: "any", l: "Cả hai" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setPrefGender(o.v)}
                        className={`min-w-[68px] flex-1 rounded-lg border px-2 py-2 text-xs transition ${
                          prefGender === o.v
                            ? "border-primary/60 bg-gold-gradient/15 text-foreground"
                            : "border-border/40 bg-background/40 text-muted-foreground"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {intents.includes("lam_an") ? (
                <div>
                  <Label className="mb-2 block text-sm">Ưu tiên giới tính đối tác (làm ăn)</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "", l: "Cả hai" },
                      { v: "male", l: "Nam" },
                      { v: "female", l: "Nữ" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setPrefGenderLamAn(o.v)}
                        className={`min-w-[68px] flex-1 rounded-lg border px-2 py-2 text-xs transition ${
                          prefGenderLamAn === o.v
                            ? "border-primary/60 bg-gold-gradient/15 text-foreground"
                            : "border-border/40 bg-background/40 text-muted-foreground"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {intents.includes("se_duyen") ? (
                <AgeRange
                  label="Độ tuổi mong muốn (se duyên)"
                  min={prefAgeMin}
                  max={prefAgeMax}
                  onMin={setPrefAgeMin}
                  onMax={setPrefAgeMax}
                />
              ) : null}
              {intents.includes("lam_an") ? (
                <AgeRange
                  label="Độ tuổi đối tác mong muốn (làm ăn)"
                  min={prefAgeMinLamAn}
                  max={prefAgeMaxLamAn}
                  onMin={setPrefAgeMinLamAn}
                  onMax={setPrefAgeMaxLamAn}
                />
              ) : null}
              {ageRangeErrors.length > 0 ? (
                <div className="space-y-1">
                  {ageRangeErrors.map((e, i) => (
                    <p key={i} className="flex items-center gap-1.5 text-xs text-amber-300">
                      <AlertCircle className="h-3.5 w-3.5" /> {e}
                    </p>
                  ))}
                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="vitri"
            data-tour={onlyOnboarding ? "seduyen-location" : undefined}
            className="overflow-hidden rounded-xl border border-border/40 bg-background/30 px-3"
          >
            <AccordionTrigger className="py-3 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> Vị trí của bạn{" "}
                <span className="text-rose-400">*</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-2 pb-4">
              <p className="text-xs text-muted-foreground">
                Bắt buộc để tính điểm tương hợp. Người khác chỉ thấy khoảng cách ước lượng, không
                thấy địa chỉ chính xác.
              </p>
              <LocationPicker value={location} onChange={setLocation} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="muctieu"
            data-tour={onlyOnboarding ? "seduyen-goals" : undefined}
            className="overflow-hidden rounded-xl border border-border/40 bg-background/30 px-3"
          >
            <AccordionTrigger className="py-3 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" /> Mục tiêu sống / giá trị{" "}
                <span className="text-rose-400">*</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              {intents.map((it) => {
                const catalog = meta.data?.goal_tags?.[it] ?? {};
                const entries = Object.entries(catalog);
                if (entries.length === 0) return null;
                const picked = goalTags[it] ?? [];
                return (
                  <div key={it}>
                    <Label className="mb-2 block text-sm">
                      {it === "se_duyen"
                        ? "Mục tiêu sống chung (se duyên)"
                        : "Mục tiêu/tầm nhìn (làm ăn)"}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {entries.map(([code, label]) => {
                        const on = picked.includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => toggleGoal(it, code)}
                            className={`rounded-full border px-3 py-1.5 text-xs transition ${
                              on
                                ? "border-primary/60 bg-gold-gradient/15 text-foreground"
                                : "border-border/40 bg-background/40 text-muted-foreground"
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="sothich"
            className="overflow-hidden rounded-xl border border-border/40 bg-background/30 px-3"
          >
            <AccordionTrigger className="py-3 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" /> Nghề nghiệp & sở thích{" "}
                <span className="text-rose-400">*</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-4">
              <p className="text-xs text-muted-foreground">
                Riêng cho se duyên — có thể khác với hồ sơ chính. Sở thích bắt buộc; nghề nghiệp bắt
                buộc thêm nếu chọn hợp tác làm ăn.
              </p>
              <div>
                <FieldLabel
                  text={`Nghề nghiệp${intents.includes("lam_an") ? " *" : ""}`}
                  field="occupation"
                  visibility={visibility}
                  onToggleVisibility={toggleVisible}
                />
                <Input
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                  placeholder="Ví dụ: Kỹ sư phần mềm"
                  maxLength={100}
                />
              </div>
              <div>
                <FieldLabel
                  text="Sở thích *"
                  field="hobbies"
                  visibility={visibility}
                  onToggleVisibility={toggleVisible}
                />
                <Input
                  value={hobbiesText}
                  onChange={(e) => setHobbiesText(e.target.value)}
                  placeholder="Cách nhau bởi dấu phẩy, ví dụ: đọc sách, du lịch, nấu ăn"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="social"
            className="overflow-hidden rounded-xl border border-border/40 bg-background/30 px-3"
          >
            <AccordionTrigger className="py-3 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary" /> Mạng xã hội
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-4">
              <FieldLabel
                text="Hiển thị mạng xã hội"
                field="social_links"
                visibility={visibility}
                onToggleVisibility={toggleVisible}
              />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SOCIAL_FIELDS.map((s) => (
                  <PersonalValue
                    key={s.key}
                    label={s.label}
                    value={personal.social_links?.[s.key]}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Link mạng xã hội sửa trong{" "}
                <button
                  type="button"
                  onClick={() => setPersonalDialogOpen(true)}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Hồ sơ cá nhân
                </button>
                .
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {validationErrors.length > 0 ? (
          <div className="space-y-1 rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2.5">
            {validationErrors.map((e, i) => (
              <p key={i} className="flex items-center gap-1.5 text-xs text-rose-300">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {e}
              </p>
            ))}
          </div>
        ) : null}

        {(() => {
          const saved = !dirty && !onlyOnboarding;
          const blocked = validationErrors.length > 0;
          return (
            <Button
              data-tour={onlyOnboarding ? "seduyen-submit" : undefined}
              onClick={() => save.mutate()}
              disabled={save.isPending || intents.length === 0 || saved || blocked}
              className={
                saved
                  ? "w-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 disabled:opacity-100"
                  : "w-full bg-gold-gradient text-primary-foreground shadow-gold hover:brightness-110"
              }
            >
              {save.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="mr-2 h-4 w-4" />
              ) : null}
              {saved
                ? "Đã lưu"
                : onlyOnboarding
                  ? "Bật hồ sơ & bắt đầu"
                  : dirty
                    ? "Lưu thay đổi"
                    : "Lưu hồ sơ"}
            </Button>
          );
        })()}

        {!onlyOnboarding && (
          <div className="pt-4 border-t border-border/40 mt-4 flex flex-col gap-2">
            <div className="text-sm">
              <div className="font-semibold text-foreground">Tạm ẩn hồ sơ</div>
              <div className="text-xs text-muted-foreground">
                Tạm thời ẩn hồ sơ của bạn khỏi danh sách khám phá. Bạn sẽ không xuất hiện để người
                khác quẹt nữa.
              </div>
            </div>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (
                  confirm(
                    "Bạn có chắc chắn muốn tạm ẩn hồ sơ kết đôi? Bạn có thể bật lại bất kỳ lúc nào bằng cách cập nhật hồ sơ.",
                  )
                ) {
                  deactivate.mutate();
                }
              }}
              disabled={deactivate.isPending}
              className="w-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 cursor-pointer"
            >
              {deactivate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Tạm ẩn hồ sơ kết đôi (Se duyên & Làm ăn)
            </Button>
          </div>
        )}
      </CardContent>

      <AvatarCropDialog
        open={cropOpen}
        onOpenChange={setCropOpen}
        file={pendingAvatarFile}
        saving={uploadingAvatar}
        onConfirm={onConfirmCroppedAvatar}
      />

      <BoideSettingsDialog
        open={personalDialogOpen}
        onOpenChange={setPersonalDialogOpen}
        onSaved={() => qc.invalidateQueries({ queryKey: ["me-profile"] })}
      />
    </Card>
  );
}
